import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { get, post, patch, del } from "../../lib/request"
import {
  formatClientWorldCityLabel,
  normalizeWorldCityRow,
} from "../../lib/worldCity"

function toNumericIds(ids) {
  if (!Array.isArray(ids)) return []
  return ids
    .map((id) => Number(id))
    .filter((n) => Number.isFinite(n))
}

function extractIdList(items, pickId) {
  if (!Array.isArray(items)) return []
  const out = []
  for (const item of items) {
    const id = pickId(item)
    if (id != null && String(id).trim() !== "") {
      out.push(String(id))
    }
  }
  return out
}

/** Map API monitor-group row to UI shape used by MonitoringGroupManagement. */
export function normalizeMonitorGroup(row) {
  if (!row || typeof row !== "object") return null
  const id = row.id
  if (id == null || String(id).trim() === "") return null

  const userIds = [
    ...extractIdList(row.monitorGroupUsers, (item) =>
      item?.user?.id ?? item?.moderator?.id ?? item?.monitorUser?.id ?? item?.userId,
    ),
    ...extractIdList(row.users, (item) => item?.id ?? item?.user?.id),
    ...extractIdList(row.moderators, (item) => item?.id ?? item?.user?.id),
    ...(Array.isArray(row.userIds) ? row.userIds.map((x) => String(x)) : []),
  ]

  const propertyIds = [
    ...extractIdList(row.monitorGroupClients, (item) =>
      item?.client?.id ?? item?.clientId,
    ),
    ...extractIdList(row.clients, (item) => item?.client?.id ?? item?.clientId ?? item?.id),
    ...(Array.isArray(row.clientIds) ? row.clientIds.map((x) => String(x)) : []),
    ...(Array.isArray(row.propertyIds) ? row.propertyIds.map((x) => String(x)) : []),
  ]

  return {
    id: String(id),
    name: row.name || "",
    description: row.description || "",
    userIds: [...new Set(userIds)],
    propertyIds: [...new Set(propertyIds)],
  }
}

function normalizeMonitorGroupList(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.data?.data)
        ? payload.data.data
        : []
  return raw.map(normalizeMonitorGroup).filter(Boolean)
}

/** Map monitor-group user client row for inbox / inbox-settings property dropdowns. */
export function normalizeMonitorGroupClient(row) {
  if (!row || typeof row !== "object") return null
  const client =
    row.client && typeof row.client === "object" ? row.client : row
  const id = client.id ?? client.clientId ?? row.clientId ?? row.id
  if (id == null || String(id).trim() === "") return null
  const worldCity = normalizeWorldCityRow(client.worldCity ?? row.worldCity)
  const name =
    String(client.name ?? client.clientName ?? row.name ?? "").trim() || "Unnamed"
  return {
    ...client,
    id,
    clientId: client.clientId ?? id,
    name,
    worldCity,
    location: formatClientWorldCityLabel(client) || formatClientWorldCityLabel(row) || null,
  }
}

function normalizeMonitorGroupClientList(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.clients)
        ? payload.clients
        : Array.isArray(payload?.data?.clients)
          ? payload.data.clients
          : []
  return raw.map(normalizeMonitorGroupClient).filter(Boolean)
}

function parseMonitorGroupClientsArg(arg) {
  if (arg != null && typeof arg === "object" && !Array.isArray(arg)) {
    return {
      userId: arg.userId,
      force: Boolean(arg.force),
    }
  }
  return {
    userId: arg,
    force: false,
  }
}

/** GET /api/v1/monitor-group/users/{userId}/clients */
export const fetchMonitorGroupClientsByUserId = createAsyncThunk(
  "monitorGroups/fetchClientsByUserId",
  async (arg, { rejectWithValue }) => {
    try {
      const { userId } = parseMonitorGroupClientsArg(arg)
      const uid =
        userId != null && String(userId).trim() !== "" ? String(userId).trim() : null
      if (!uid) return rejectWithValue("userId is required")
      const res = await get(`monitor-groups/users/${uid}/clients`)
      return normalizeMonitorGroupClientList(res)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch monitor group clients")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { userId, force } = parseMonitorGroupClientsArg(arg)
      if (force) return true
      const mg = getState()?.monitorGroups
      if (!mg || mg.userClientsStatus === "loading") return false
      const uid = userId != null ? String(userId).trim() : ""
      if (!uid) return false
      if (
        mg.userClientsStatus === "succeeded" &&
        String(mg.userClientsUserId ?? "") === uid &&
        Array.isArray(mg.userClients) &&
        mg.userClients.length > 0
      ) {
        return false
      }
      return true
    },
  },
)

/** GET /api/v1/monitor-group/organizations/{orgId} */
export const fetchMonitorGroupsByOrgId = createAsyncThunk(
  "monitorGroups/fetchByOrgId",
  async (orgId, { rejectWithValue }) => {
    try {
      if (!orgId) return rejectWithValue("orgId is required")
      const res = await get(`monitor-groups/organizations/${orgId}`)
      return normalizeMonitorGroupList(res)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch monitor groups")
    }
  },
)

/** POST /api/v1/monitor-group/organizations/{orgId} */
export const createMonitorGroup = createAsyncThunk(
  "monitorGroups/create",
  async ({ name, description, orgId, userIds, clientIds }, { rejectWithValue }) => {
    try {
      const trimmedName = String(name || "").trim()
      if (!trimmedName) return rejectWithValue("Group name is required")
      if (!orgId) return rejectWithValue("orgId is required")

      const body = {
        name: trimmedName,
        description: String(description || "").trim(),
        userIds: toNumericIds(userIds),
        clientIds: toNumericIds(clientIds),
      }

      const res = await post(`monitor-groups/organizations/${orgId}`, body)
      return normalizeMonitorGroup(res?.data ?? res) ?? {
        id: String(res?.id ?? res?.data?.id ?? ""),
        name: trimmedName,
        description: body.description,
        userIds: (userIds || []).map(String),
        propertyIds: (clientIds || []).map(String),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create monitor group")
    }
  },
)

/** PATCH /api/v1/monitor-group/{id} */
export const updateMonitorGroup = createAsyncThunk(
  "monitorGroups/update",
  async ({ id, name, description, userIds, clientIds }, { rejectWithValue }) => {
    try {
      if (id == null || String(id).trim() === "") {
        return rejectWithValue("Group id is required")
      }
      const trimmedName = String(name || "").trim()
      if (!trimmedName) return rejectWithValue("Group name is required")

      const body = {
        name: trimmedName,
        description: String(description || "").trim(),
        userIds: toNumericIds(userIds),
        clientIds: toNumericIds(clientIds),
      }

      const res = await patch(`monitor-groups/${id}`, body)
      return (
        normalizeMonitorGroup(res?.data ?? res) ?? {
          id: String(id),
          name: trimmedName,
          description: body.description,
          userIds: (userIds || []).map(String),
          propertyIds: (clientIds || []).map(String),
        }
      )
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update monitor group")
    }
  },
)

/** DELETE /api/v1/monitor-group/{id} */
export const deleteMonitorGroup = createAsyncThunk(
  "monitorGroups/delete",
  async (id, { rejectWithValue }) => {
    try {
      if (id == null || String(id).trim() === "") {
        return rejectWithValue("Group id is required")
      }
      await del(`monitor-groups/${id}`)
      return { id: String(id) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete monitor group")
    }
  },
)

const monitorGroupSlice = createSlice({
  name: "monitorGroups",
  initialState: {
    list: [],
    status: "idle",
    error: null,
    organizationId: null,
    userClients: [],
    userClientsStatus: "idle",
    userClientsError: null,
    userClientsUserId: null,
    mutateStatus: "idle",
    mutateError: null,
    deleteStatus: "idle",
    deleteError: null,
  },
  reducers: {
    clearMonitorGroups: (state) => {
      state.list = []
      state.status = "idle"
      state.error = null
      state.organizationId = null
      state.mutateStatus = "idle"
      state.mutateError = null
      state.deleteStatus = "idle"
      state.deleteError = null
    },
    clearMonitorGroupMutateError: (state) => {
      state.mutateError = null
    },
    clearMonitorGroupDeleteError: (state) => {
      state.deleteError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonitorGroupsByOrgId.pending, (state, action) => {
        const nextOrgId = action.meta.arg != null ? String(action.meta.arg) : null
        if (nextOrgId == null) return
        const prevOrgId =
          state.organizationId != null ? String(state.organizationId) : null
        const isSameOrgRefresh =
          prevOrgId !== null && prevOrgId === nextOrgId && state.status === "succeeded"
        if (isSameOrgRefresh) {
          state.organizationId = nextOrgId
          return
        }
        state.status = "loading"
        state.error = null
        if (prevOrgId !== null && prevOrgId !== nextOrgId) {
          state.list = []
        }
        state.organizationId = nextOrgId
      })
      .addCase(fetchMonitorGroupsByOrgId.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.list = action.payload || []
        state.organizationId = action.meta.arg != null ? String(action.meta.arg) : null
      })
      .addCase(fetchMonitorGroupsByOrgId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch monitor groups"
      })
      .addCase(fetchMonitorGroupClientsByUserId.pending, (state, action) => {
        const { userId, force } = parseMonitorGroupClientsArg(action.meta.arg)
        const nextUserId = userId != null ? String(userId) : null
        if (nextUserId == null) return
        const prevUserId =
          state.userClientsUserId != null ? String(state.userClientsUserId) : null
        const isSameUserRefresh =
          !force &&
          prevUserId !== null &&
          prevUserId === nextUserId &&
          state.userClientsStatus === "succeeded"
        if (isSameUserRefresh) {
          state.userClientsUserId = nextUserId
          return
        }
        state.userClientsStatus = "loading"
        state.userClientsError = null
        if (prevUserId !== null && prevUserId !== nextUserId) {
          state.userClients = []
        }
        state.userClientsUserId = nextUserId
      })
      .addCase(fetchMonitorGroupClientsByUserId.fulfilled, (state, action) => {
        state.userClientsStatus = "succeeded"
        state.userClients = action.payload || []
        const { userId } = parseMonitorGroupClientsArg(action.meta.arg)
        state.userClientsUserId = userId != null ? String(userId) : null
      })
      .addCase(fetchMonitorGroupClientsByUserId.rejected, (state, action) => {
        state.userClientsStatus = "failed"
        state.userClientsError =
          action.payload || "Failed to fetch monitor group clients"
      })
      .addCase(createMonitorGroup.pending, (state) => {
        state.mutateStatus = "loading"
        state.mutateError = null
      })
      .addCase(createMonitorGroup.fulfilled, (state) => {
        state.mutateStatus = "succeeded"
      })
      .addCase(createMonitorGroup.rejected, (state, action) => {
        state.mutateStatus = "failed"
        state.mutateError = action.payload || "Failed to create monitor group"
      })
      .addCase(updateMonitorGroup.pending, (state) => {
        state.mutateStatus = "loading"
        state.mutateError = null
      })
      .addCase(updateMonitorGroup.fulfilled, (state) => {
        state.mutateStatus = "succeeded"
      })
      .addCase(updateMonitorGroup.rejected, (state, action) => {
        state.mutateStatus = "failed"
        state.mutateError = action.payload || "Failed to update monitor group"
      })
      .addCase(deleteMonitorGroup.pending, (state) => {
        state.deleteStatus = "loading"
        state.deleteError = null
      })
      .addCase(deleteMonitorGroup.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded"
        const deletedId = String(action.payload?.id ?? "")
        if (deletedId) {
          state.list = state.list.filter((g) => String(g.id) !== deletedId)
        }
      })
      .addCase(deleteMonitorGroup.rejected, (state, action) => {
        state.deleteStatus = "failed"
        state.deleteError = action.payload || "Failed to delete monitor group"
      })
  },
})

export const {
  clearMonitorGroups,
  clearMonitorGroupMutateError,
  clearMonitorGroupDeleteError,
} = monitorGroupSlice.actions
export default monitorGroupSlice.reducer
